(function () {
  class VEHomeBestSellers {
    constructor(section) {
      this.section = section;
      this.host = section.closest(".section-home-best-sellers") || section;
      this.cards = Array.from(section.querySelectorAll("[data-builder-product]"));
      this.uniqueCards = Array.from(
        this.cards.reduce((map, card) => {
          const variantId = String(card.dataset.variantId || "");
          if (variantId && !map.has(variantId)) {
            map.set(variantId, card);
          }
          return map;
        }, new Map()).values()
      );
      this.cardMap = new Map(this.uniqueCards.map((card) => [String(card.dataset.variantId), card]));
      this.items = new Map();
      this.syncQueue = Promise.resolve();
      this.cartUpdateUnsubscriber = null;
      this.track = section.querySelector("[data-carousel-track]");
      this.prevButton = section.querySelector("[data-carousel-prev]");
      this.nextButton = section.querySelector("[data-carousel-next]");
      this.boundUpdateCarouselState = this.updateCarouselState.bind(this);

      this.bind();
      this.setupCarousel();
      this.enqueueSyncTask(() => this.hydrateFromCart());
    }

    bind() {
      this.section.addEventListener("click", (event) => {
        const carouselButton = event.target.closest("[data-carousel-prev], [data-carousel-next]");
        if (carouselButton) {
          event.preventDefault();
          this.handleCarouselButton(carouselButton);
          return;
        }

        const addButton = event.target.closest("[data-builder-add]");
        if (addButton) {
          const card = addButton.closest("[data-builder-product]");
          if (card?.dataset.variantId) this.changeQuantity(card.dataset.variantId, 1);
          return;
        }

        const increaseButton = event.target.closest("[data-builder-increase]");
        if (increaseButton) {
          const card = increaseButton.closest("[data-builder-product]");
          if (card?.dataset.variantId) this.changeQuantity(card.dataset.variantId, 1);
          return;
        }

        const decreaseButton = event.target.closest("[data-builder-decrease]");
        if (decreaseButton) {
          const card = decreaseButton.closest("[data-builder-product]");
          if (card?.dataset.variantId) this.changeQuantity(card.dataset.variantId, -1);
        }
      });

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === "home-best-sellers") return;
          this.enqueueSyncTask(() => this.hydrateFromCart());
        });
      }

      document.addEventListener("cart:updated", (event) => {
        if (event?.detail?.source === "home-best-sellers") return;
        this.enqueueSyncTask(() => this.hydrateFromCart());
      });

      window.addEventListener("pageshow", () => this.enqueueSyncTask(() => this.hydrateFromCart()));
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        this.enqueueSyncTask(() => this.hydrateFromCart());
      });
    }

    enqueueSyncTask(task) {
      this.syncQueue = this.syncQueue.catch(() => undefined).then(task);
      return this.syncQueue;
    }

    setupCarousel() {
      if (!this.track || !this.prevButton || !this.nextButton) return;

      this.track.addEventListener("scroll", this.boundUpdateCarouselState, { passive: true });
      window.addEventListener("resize", this.boundUpdateCarouselState);
      window.addEventListener("load", this.boundUpdateCarouselState);

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(this.boundUpdateCarouselState);
        this.resizeObserver.observe(this.track);
      }

      window.setTimeout(this.boundUpdateCarouselState, 80);
    }

    getStepSize() {
      const firstCard = this.track?.querySelector("[data-builder-product]");
      if (!firstCard) return 320;

      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || "0");

      return firstCard.getBoundingClientRect().width + gap;
    }

    handleCarouselButton(button) {
      if (!this.track) return;

      const direction = button.hasAttribute("data-carousel-next") ? 1 : -1;
      this.track.scrollBy({
        left: this.getStepSize() * direction,
        behavior: "smooth",
      });
    }

    updateCarouselState() {
      if (!this.track || !this.prevButton || !this.nextButton) return;

      const maxScrollLeft = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const currentScrollLeft = this.track.scrollLeft;
      const hasOverflow = maxScrollLeft > 8;

      this.host?.classList.toggle("is-carousel-overflowing", hasOverflow);
      this.prevButton.disabled = !hasOverflow || currentScrollLeft <= 8;
      this.nextButton.disabled = !hasOverflow || currentScrollLeft >= maxScrollLeft - 8;
    }

    getCartDrawer() {
      return document.querySelector("cart-drawer");
    }

    getCartNotification() {
      return document.querySelector("cart-notification");
    }

    getSectionsToRender() {
      const sections = [];
      const cartDrawer = this.getCartDrawer();

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        const availableSections = cartDrawer
          .getSectionsToRender()
          .filter((section) => {
            if (section.selector) {
              return Boolean(document.querySelector(section.selector));
            }
            return Boolean(document.getElementById(section.id));
          })
          .map((section) => section.id);

        sections.push(...availableSections);
      }

      if (document.getElementById("cart-notification-product")) {
        sections.push("cart-notification-product");
      }
      if (document.getElementById("cart-notification-button")) {
        sections.push("cart-notification-button");
      }
      if (document.getElementById("cart-icon-bubble")) {
        sections.push("cart-icon-bubble");
      }

      return [...new Set(sections)];
    }

    getCartAddUrl() {
      if (window.routes && window.routes.cart_add_url) {
        return window.routes.cart_add_url;
      }

      return "/cart/add.js";
    }

    getCartChangeUrl() {
      if (window.routes && window.routes.cart_change_url) {
        return window.routes.cart_change_url;
      }

      return "/cart/change.js";
    }

    isHomeManagedVariantId(variantId) {
      return this.cardMap.has(String(variantId || ""));
    }

    async fetchCart() {
      const response = await fetch("/cart.js", {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || "No se pudo leer el carrito.");
      }

      return data;
    }

    async hydrateFromCart() {
      const cart = await this.fetchCart();
      this.items.clear();

      cart.items.forEach((item) => {
        /* Skip line items that belong to a pack created in
           /pages/crea-tu-pedido. Those are managed by the builder
           and shouldn't be visible to the home stepper — otherwise
           the stepper would let the customer modify pack item
           quantities by clicking +/- on a home card, which breaks
           the "1 box + 3 bottles" coherence. With the filter, the
           home stepper shows qty=0 for those variants (so the card
           shows the "Sumar al pedido" button instead of the
           stepper); clicking that button adds a new STAND-ALONE line
           item (no _ve_pack_id), leaving the pack untouched. */
        if (item.properties && item.properties._ve_pack_id) return;
        if (!this.isHomeManagedVariantId(item?.variant_id)) return;
        const variantId = String(item.variant_id);
        const currentQuantity = this.items.get(variantId) || 0;
        this.items.set(variantId, currentQuantity + (Number(item.quantity) || 0));
      });

      this.renderCards();
    }

    renderCard(card) {
      const variantId = String(card.dataset.variantId || "");
      const quantity = this.items.get(variantId) || 0;
      const addButton = card.querySelector("[data-builder-add]");
      const stepper = card.querySelector(".ve-builder-card__stepper");
      const quantityLabel = card.querySelector("[data-builder-quantity]");
      const increase = card.querySelector("[data-builder-increase]");
      const decrease = card.querySelector("[data-builder-decrease]");

      if (quantityLabel) quantityLabel.textContent = String(quantity);
      if (addButton) {
        addButton.hidden = quantity > 0;
        addButton.disabled = false;
      }
      if (stepper) stepper.hidden = quantity === 0;
      if (increase) increase.disabled = false;
      if (decrease) decrease.disabled = quantity === 0;

      card.classList.toggle("is-selected", quantity > 0);
      card.classList.toggle("is-added", quantity > 0);
    }

    renderCards() {
      this.cards.forEach((card) => this.renderCard(card));
    }

    changeQuantity(variantId, delta) {
      const normalizedVariantId = String(variantId || "");
      if (!this.isHomeManagedVariantId(normalizedVariantId)) return;

      const current = this.items.get(normalizedVariantId) || 0;
      const next = current + delta;

      if (next <= 0) {
        this.items.delete(normalizedVariantId);
      } else {
        this.items.set(normalizedVariantId, next);
      }

      this.renderCards();
      this.queueCartSync();
    }

    queueCartSync() {
      return this.enqueueSyncTask(async () => {
        try {
          await this.syncHomeItemsToCart();
        } catch (error) {
          console.error(error);
          await this.hydrateFromCart();
        }
      });
    }

    async requestCartAdd(variantId, quantity) {
      const response = await fetch(this.getCartAddUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity,
            },
          ],
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || "No se pudo sincronizar el carrito.");
      }

      return data;
    }

    async requestCartChange(lineKey, quantity) {
      const response = await fetch(this.getCartChangeUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || "No se pudo sincronizar el carrito.");
      }

      return data;
    }

    async syncHomeItemsToCart() {
      const cart = await this.fetchCart();
      const cartLinesByVariant = new Map();

      cart.items.forEach((item) => {
        /* Pack items are off-limits to the home stepper (see comment
           in hydrateFromCart). Treat them as invisible so the sync
           never tries to modify a pack item via /cart/change.js. If
           the customer wants to add another bottle of the same variant
           as a stand-alone, requestCartAdd below creates a NEW line
           item without the pack properties. */
        if (item.properties && item.properties._ve_pack_id) return;
        if (!this.isHomeManagedVariantId(item?.variant_id)) return;
        const variantId = String(item.variant_id);
        if (!cartLinesByVariant.has(variantId)) {
          cartLinesByVariant.set(variantId, []);
        }
        cartLinesByVariant.get(variantId).push(item);
      });

      let latestResponse = null;
      let latestVariantId = null;

      for (const [variantId, quantity] of this.items.entries()) {
        const normalizedVariantId = String(variantId);
        const desiredQuantity = Number(quantity) || 0;
        const existingLines = cartLinesByVariant.get(normalizedVariantId) || [];

        if (existingLines.length === 0) {
          if (desiredQuantity > 0) {
            latestResponse = await this.requestCartAdd(normalizedVariantId, desiredQuantity);
            latestVariantId = normalizedVariantId;
          }
        } else {
          const [primaryLine, ...duplicatedLines] = existingLines;
          if (Number(primaryLine.quantity) !== desiredQuantity) {
            latestResponse = await this.requestCartChange(primaryLine.key, desiredQuantity);
            latestVariantId = normalizedVariantId;
          }

          for (const duplicatedLine of duplicatedLines) {
            latestResponse = await this.requestCartChange(duplicatedLine.key, 0);
            latestVariantId = normalizedVariantId;
          }
        }

        cartLinesByVariant.delete(normalizedVariantId);
      }

      for (const [variantId, lines] of cartLinesByVariant.entries()) {
        for (const line of lines) {
          latestResponse = await this.requestCartChange(line.key, 0);
          latestVariantId = variantId;
        }
      }

      if (latestResponse) {
        if (!latestResponse.sections) {
          latestResponse = await this.fetchSectionsForNativeCart();
        }
        this.updateNativeCart(latestResponse, latestVariantId);
      } else {
        const refreshedSections = await this.fetchSectionsForNativeCart();
        this.updateNativeCart(refreshedSections, null);
      }
    }

    getDrawerEmptyState(response) {
      if (typeof response?.item_count === "number") {
        return response.item_count === 0;
      }

      const drawerMarkup = response?.sections?.["cart-drawer"];
      if (!drawerMarkup) return null;

      const parsed = new DOMParser().parseFromString(drawerMarkup, "text/html");
      const drawerFromSection = parsed.querySelector("cart-drawer");
      if (drawerFromSection) {
        return drawerFromSection.classList.contains("is-empty");
      }

      const drawerItemsFromSection = parsed.querySelector("cart-drawer-items");
      if (drawerItemsFromSection) {
        return drawerItemsFromSection.classList.contains("is-empty");
      }

      return null;
    }

    syncNativeDrawerEmptyState(response) {
      const isEmpty = this.getDrawerEmptyState(response);
      if (isEmpty === null) return;

      const cartDrawer = this.getCartDrawer();
      const cartDrawerItems = document.querySelector("cart-drawer-items");
      const drawerInner = cartDrawer?.querySelector(".drawer__inner");

      if (cartDrawer) {
        cartDrawer.classList.toggle("is-empty", isEmpty);
      }

      if (cartDrawerItems) {
        cartDrawerItems.classList.toggle("is-empty", isEmpty);
      }

      if (drawerInner) {
        drawerInner.classList.toggle("is-empty", isEmpty);
      }
    }

    renderFallbackBubble(response) {
      const bubbleMarkup = response?.sections?.["cart-icon-bubble"];
      const bubble = document.getElementById("cart-icon-bubble");

      if (!bubbleMarkup || !bubble) return;

      const parsed = new DOMParser().parseFromString(bubbleMarkup, "text/html");
      const replacement = parsed.querySelector(".shopify-section") || parsed.getElementById("cart-icon-bubble");

      if (replacement) {
        bubble.innerHTML = replacement.innerHTML;
      }
    }

    async fetchSectionsForNativeCart() {
      const sectionIds = this.getSectionsToRender();
      if (!sectionIds.length) {
        return { sections: {} };
      }

      const response = await fetch(`?sections=${sectionIds.join(",")}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error("No se pudo actualizar el carrito visualmente.");
      }

      const sections = await response.json();
      return { sections };
    }

    updateNativeCart(response, variantId) {
      const cartDrawer = this.getCartDrawer();
      const cartNotification = this.getCartNotification();
      const hasCartDrawerSection = Boolean(response?.sections?.["cart-drawer"]);
      const hasBubbleSection = Boolean(response?.sections?.["cart-icon-bubble"]);
      const cartDrawerContainer = document.querySelector("#CartDrawer");
      const cartBubble = document.getElementById("cart-icon-bubble");
      const canRenderDrawer = Boolean(cartDrawer && cartDrawerContainer && hasCartDrawerSection);

      const canRenderNotification = Boolean(
        cartNotification &&
          document.getElementById("cart-notification-product") &&
          document.getElementById("cart-notification-button") &&
          cartBubble &&
          response?.sections?.["cart-notification-product"] &&
          response?.sections?.["cart-notification-button"] &&
          hasBubbleSection
      );

      if (canRenderDrawer && typeof cartDrawer.renderContents === "function") {
        cartDrawer.renderContents(response, true);
      } else if (canRenderNotification && typeof cartNotification.renderContents === "function") {
        cartNotification.renderContents(response);
      } else {
        this.renderFallbackBubble(response);
      }

      this.syncNativeDrawerEmptyState(response);

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: {
            source: "home-best-sellers",
            productVariantId: variantId,
            cartData: response,
          },
        })
      );

      try {
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "home-best-sellers",
            productVariantId: variantId,
            cartData: response,
          });
        }
      } catch (error) {
        console.warn("Could not publish internal cart update event.", error);
      }
    }
  }

  document.querySelectorAll("[data-ve-best-sellers]").forEach((section) => {
    if (section.dataset.bestSellersReady === "true") return;
    section.dataset.bestSellersReady = "true";
    new VEHomeBestSellers(section);
  });
})();
